import { Menu, Modal, Tooltip } from '@mantine/core'
import PrimaryDropDown from 'app/views/components/DropDown/DropDown'
import { useEffect, useState } from 'react'
import { DealParams } from 'app/models/deal-params'
import { useDispatch } from 'react-redux'
import { FacilityParams } from 'app/models/facility-params'
import { Hold } from 'app/models/hold-params'
import { UpdatedFacilitiesScreen } from '../updated-facilities-screen'
import {
    approveFacility,
    approveFacilityAmendment,
    deleteFacility,
    deleteFacilityAmendment,
    rejectFacility,
    rejectFacilityAmendment,
    submitFacility,
    submitFacilityAmendment,
} from './facility-daml-actions'
import { loadLendersPositionsByFacility } from 'app/state/ducks/lenders-positions/thunks'
import { loadOneDeal } from 'app/state/ducks/deals/thunks'
import HistoryForm from './history/history'
import { useNavigate } from 'react-router-dom'
import { loadFeesByFacility } from 'app/state/ducks/fees/thunks'
import LetterOfCreditForm from '../../letter-of-credit/letter-of-credit-form-logic'
import { loadSchedulerAmortizationByFacility } from 'app/state/ducks/amortization/thunks'
import { UpdatedLoanScreen } from '../../loans/loan-stepper'
import IonIcon from '@reacticons/ionicons'
import FacilityTerminationForm from '../locked-facilities/facility-termination-form'
import { fetchOneFacility, loadFacilitySnapshot } from 'app/state/ducks/facilities/thunks'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import { generateHTML } from '../../deals/preview/generateHTML'
import { formatDateSnapshot } from 'app/utils/util-functions'
import PrimaryInput from 'app/views/components/buttons/PrimaryInput'
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation'
import {
    loadNullFacilityAmendment,
    loadOneFacilityAmendment,
} from 'app/state/ducks/facility-amendments/thunks'
import { loadFacilityFormLists, loadLoanFormLists } from 'app/state/utils/form-lists'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import PlaceHoldModal from 'app/views/components/holds/place-hold-modal'
import LiftHoldModal from 'app/views/components/holds/lift-hold-modal'
import { HoldPayload } from 'app/views/components/holds/hold-form-utils'
import {
    placeFacilityHoldAction,
    liftFacilityHoldAction,
} from './facility-hold-actions'

type Props = {
    row: FacilityParams
    withRedirection?: boolean
    extended?: boolean
    primaryInput?: boolean
    loading?: boolean
}

// Gates the "Place Lock" action: a facility already carrying an active,
// directly-placed (not cascaded) LendOS Facility-level hold can't be locked
// again until that hold is lifted.
export const facilityAlreadyLocked = (holds: Hold[]) =>
    holds.some(h => !h.liftedAt && h.system === 'LendOS' && h.origin.level === 'Facility')

export default function FacilityActions({
    row,
    withRedirection,
    extended,
    primaryInput,
    loading
}: Props) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteType, setDeleteType] = useState<'facility' | 'amendment' | null>(null);
    const [openEdit, setOpenEdit] = useState(false)
    const [openTerminate, setOpenTerminate] = useState(false)
    const [facility, setFacility] = useState<FacilityParams | undefined>(row)
    const [deal, setDeal] = useState<DealParams | undefined>(undefined)
    const [openHistory, setOpenHistory] = useState(false)
    const [loanOpen, setLoanOpen] = useState(false)
    const [letterOfCreditOpen, setLetterOfCreditOpen] = useState(false)
    const [facilityAmendment, setFacilityAmendment] =
        useState<FacilityParams | null>(null)
    const [amendment, setAmendment] = useState<string | null>(null)
    const [showPlaceHold, setShowPlaceHold] = useState(false)
    const [showLiftHold, setShowLiftHold] = useState(false)
    const [step, setStep] = useState<number>(0)

    // The facility's own active LendOS lock, if any — passed to the Lift modal
    // (cascaded/SigningPortal holds aren't liftable from here, see
    // facilityAlreadyLocked/FacilityHoldIndicator).
    const activeFacilityHold =
        (row?.holds ?? []).find(
            h => !h.liftedAt && h.system === 'LendOS' && h.origin.level === 'Facility',
        ) ?? null
    const isAlreadyLocked = facilityAlreadyLocked(row?.holds ?? [])

    useEffect(() => {
        setFacility(row);
    }, [row])

    // The forms take the whole record; a grid row is a summary (ADR_LOS-030).
    // The record is fetched FIRST, when a form opens, and the form opens with
    // it — the forms build their values once from what they open with. The
    // deal it draws on, the amendment in flight and the lists the form offers
    // are fetched the same way: the page that hosts the grid loads no list.
    const [loadingFacility, setLoadingFacility] = useState(false)

    /**
     * The deal the facility draws on. Approved for nearly every facility, but a
     * draft facility can sit on a deal still in Draft or Submitted (the deal's
     * anchor exists from its draft), so those states are tried after, and a
     * terminated deal last — its facilities can still be worked on. Undefined
     * when the row names none or it cannot be loaded in any state; the caller
     * decides whether that stops the form (see dealMissing).
     */
    async function fetchDeal(): Promise<DealParams | undefined> {
        const dealId = row?.dealId?.id
        if (!dealId) return undefined
        for (const status of ['Approved', 'Submitted', 'Draft', 'Terminated']) {
            const result: any = await dispatch(loadOneDeal(dealId, status))
            if (result?.payload?.id) return result.payload
        }
        return undefined
    }

    /**
     * Whether a form must stay closed for want of its deal: the row names one
     * and none loaded. ONE notice, raised here rather than in fetchDeal, so a
     * click whose facility fetch already failed (and notified) does not stack
     * a second toast. The form reads the deal's pricing-grid flag and
     * borrowers: without it, it would open with a step silently disabled.
     */
    function dealMissing(fetchedDeal: DealParams | undefined): boolean {
        if (!row?.dealId?.id || fetchedDeal) return false
        ErrorNotification({
            title: 'Could not open facility',
            message: 'The deal this facility draws on could not be loaded.',
        })
        return true
    }

    /**
     * The amendment standing in for the facility, by its own id, when the row
     * is one. Null otherwise, which also clears the amendment the form reads.
     */
    async function fetchAmendment(): Promise<FacilityParams | null> {
        if (!row?.amendment) {
            dispatch(loadNullFacilityAmendment())
            return null
        }
        const result: any = await dispatch(
            loadOneFacilityAmendment(row.transactionId ?? row.id, row.status ?? 'Draft'),
        )
        if (!result?.payload?.id) {
            ErrorNotification({
                title: 'Could not open amendment',
                message: typeof result?.payload === 'string' ? result.payload : 'The amendment could not be loaded.',
            })
            return null
        }
        return result.payload
    }

    /**
     * Load the whole record for a form. Resolves to false, with a notice, when
     * it cannot be loaded — the caller then leaves the form closed.
     */
    async function loadFullFacility(): Promise<boolean> {
        if (!row?.id || !row.status) return false
        setLoadingFacility(true)
        try {
            // an amendment row carries the amendment's state; the record it
            // stands in for is the approved facility
            const result: any = await dispatch(
                fetchOneFacility(row.id, row.amendment ? 'Approved' : row.status),
            )
            if (!result?.success) {
                ErrorNotification({
                    title: 'Could not open facility',
                    message: result?.payload || 'The facility could not be loaded.',
                })
                return false
            }
            setFacility(result.payload)
            return true
        } finally {
            setLoadingFacility(false)
        }
    }

    async function editFacility(facilityId: string, step?: number) {
        if (loadingFacility) return
        dispatch(loadLendersPositionsByFacility(facilityId))
        dispatch(loadFeesByFacility(facilityId))
        dispatch(loadSchedulerAmortizationByFacility(facilityId))
        setAmendment(null)
        setStep(step ?? 0)
        const [loaded, fetchedDeal] = await Promise.all([
            loadFullFacility(),
            fetchDeal(),
            dispatch(loadFacilityFormLists() as any),
        ])
        if (!loaded || dealMissing(fetchedDeal)) return
        setDeal(fetchedDeal)
        setOpenEdit(true)
    }

    async function amendFacility(facilityId: string, type: string, step?: number) {
        if (loadingFacility) return
        dispatch(loadLendersPositionsByFacility(facilityId))
        dispatch(loadFeesByFacility(facilityId))
        dispatch(loadSchedulerAmortizationByFacility(facilityId))
        setAmendment(type)
        setStep(step ?? 0)
        const [loaded, fetchedDeal, amended] = await Promise.all([
            loadFullFacility(),
            fetchDeal(),
            fetchAmendment(),
            dispatch(loadFacilityFormLists() as any),
        ])
        if (!loaded || (row.amendment && !amended) || dealMissing(fetchedDeal)) return
        setDeal(fetchedDeal)
        setFacilityAmendment(amended)
        setOpenEdit(true)
    }

    async function terminateFacility() {
        if (loadingFacility) return
        const [loaded, amended] = await Promise.all([loadFullFacility(), fetchAmendment()])
        if (!loaded || (row.amendment && !amended)) return
        setFacilityAmendment(amended)
        setOpenTerminate(true)
    }

    function displayHistory(): any {
        // the history's modified-fields view names entities from the entity
        // store, which the page that hosts the grid no longer loads (ADR_LOS-030)
        dispatch(loadEntities())
        setOpenHistory(true)
    }

    async function openLoanForm() {
        const [loaded] = await Promise.all([loadFullFacility(), dispatch(loadLoanFormLists() as any)])
        if (loaded) setLoanOpen(true)
    }

    async function submitCurrentFacility() {
        const result = await submitFacility(row, dispatch)
        if (result && withRedirection) {
            navigate(`/dealmanagement/facilities/${row.id}/Submitted`)
        }
    }

    async function approveCurrentFacility() {
        const result = await approveFacility(row, dispatch)
        if (result && withRedirection) {
            navigate(`/dealmanagement/facilities/${row.id}/Approved`)
        }
    }

    async function rejectCurrentFacility() {
        const result = await rejectFacility(row, dispatch)
        if (result && withRedirection) {
            navigate(`/dealmanagement/facilities/${row.id}/Draft`)
        }
    }
    async function submitCurrentFacilityAmendment() {
        const result = await submitFacilityAmendment(row, dispatch)
        if (result && withRedirection) {
            navigate(`/dealmanagement/facilities/${row.id}/Subbmitted`)
        }
    }
    async function approveCurrentFacilityAmendment() {
        const result = await approveFacilityAmendment(row, dispatch)
        if (result && withRedirection) {
            if (row?.amendmentType?.toLowerCase() === 'termination') {
                navigate(`/dealmanagement/facilities/${row.id}/Terminated`)
            } else {
                navigate(`/dealmanagement/facilities/${row.id}/Approved`)
            }
        }
    }
    async function rejectCurrentFacilityAmendment() {
        const result = await rejectFacilityAmendment(row, dispatch)
        if (result && withRedirection) {
            navigate(`/dealmanagement/facilities/${row.id}/Draft`)
        }
    }

    async function openAmortizationSchedule() {
        navigate(`/dealmanagement/facilities/${row.id}/${row.status}/amortization-schedule`)
    }

    const handleDelete = async () => {
        if (deleteType === 'facility') {
            await deleteFacility(row, dispatch);
            if (withRedirection) {
                navigate(`/dealmanagement/facilities`);
            }
        } else if (deleteType === 'amendment') {
            await deleteFacilityAmendment(row, dispatch);
            if (withRedirection) {
                navigate(`/dealmanagement/facilities/${row.id}/Approved`);
            }
        }
        setShowDeleteModal(false);
    };



    const openPreview = async (row: any) => {
        const facilitySnapshot = await loadFacilitySnapshot(row.id)
        row = facilitySnapshot.payload
        const { monthDayYear } = formatDateSnapshot();
        let previewHTML = ""
        previewHTML = `
    <!DOCTYPE html>
      <html>
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Facility</title>
            <link rel="stylesheet" type="text/css" href="/previewStyles.css" />
        </head>
        <body>
            <div id="content">
                <header>
                    <div>
                        <h1>Facility Snapshot</h1>
                    </div>
                    <div>As of ${monthDayYear}</div>
                </header>
    
                <main>
                    ${generateHTML(row, 1)}
                </main>
            </div>
        </body>
      </html>`;

        const newWindow = window.open("", "_blank")
        if (newWindow) {
            newWindow.document.write(previewHTML)
            newWindow.document.close()
        }
    }

    return (
        <div>
            <Modal
                className="modal-body modal-body-template fit-content-modal"
                opened={openEdit}
                onClose={() => setOpenEdit(false)}
                centered={true}
                size="77%"
            >
                {amendment ? (
                    <UpdatedFacilitiesScreen
                        deal={deal}
                        facility={facilityAmendment ?? facility}
                        step={step}
                        onClose={() => setOpenEdit(false)}
                        amendment={amendment}
                    />
                ) : (
                    <UpdatedFacilitiesScreen
                        deal={deal}
                        facility={facility}
                        step={step}
                        onClose={() => setOpenEdit(false)}
                    />
                )}
            </Modal>
            <Modal
                className="modal-body modal-body-template fit-content-modal"
                size="xl"
                // style={{ height: '80%' }}
                opened={openTerminate}
                onClose={() => setOpenTerminate(false)}
            >
                {facility && (
                    <FacilityTerminationForm
                        facility={facilityAmendment ?? facility}
                        close={() => setOpenTerminate(false)}
                    />
                )}
            </Modal>
            <Menu shadow="md" width={primaryInput ? '250px' : 200} position="bottom" withinPortal>
                <Menu.Target>
                    <div style={{ width: 'fit-content' }}>
                        {primaryInput ?
                            <PrimaryInput w='159px'>
                                {loadingFacility ? 'Loading…' : 'Actions'}
                                <IonIcon name='chevron-down-outline' className='primaryInputChevronDown' />
                            </PrimaryInput>
                            :
                            <PrimaryDropDown>{loadingFacility ? 'Loading…' : 'Actions'}</PrimaryDropDown>
                        }
                    </div>
                </Menu.Target>


                <Menu.Dropdown>
                    {row?.status?.toLowerCase() == 'approved' ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                            onClick={() => openPreview(row)}
                        >
                            Snapshot
                        </Menu.Item>
                    ) : null}
                    {row?.status?.toLowerCase() == 'approved' ? (
                        <Tooltip
                            label="This facility is already locked"
                            disabled={!isAlreadyLocked}
                            withinPortal
                        >
                            <div>
                                <Menu.Item disabled={loading || isAlreadyLocked}
                                    icon={<IonIcon className="action-ion-icon" name='lock-closed-outline' />}
                                    onClick={() => setShowPlaceHold(true)}
                                >
                                    Place Lock
                                </Menu.Item>
                            </div>
                        </Tooltip>
                    ) : null}
                    {row?.status?.toLowerCase() == 'approved' ? (
                        <Menu.Item disabled={loading || !activeFacilityHold}
                            icon={<IonIcon className="action-ion-icon" name='lock-open-outline' />}
                            onClick={() => setShowLiftHold(true)}
                        >
                            Lift Lock
                        </Menu.Item>
                    ) : null}
                    {row?.status?.toLowerCase() == 'draft' && !row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                            onClick={() => editFacility(row.id)}
                        >
                            Edit
                        </Menu.Item>
                    ) : null}
                    {row?.amortizationScheduleId ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='calendar-outline' />}
                            onClick={openAmortizationSchedule}
                        >
                            Schedule
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'draft' && row?.amendment ? (
                        row?.amendmentType?.toLowerCase() === 'termination' ? (
                            <Menu.Item disabled={loading}
                                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                onClick={() => {
                                    terminateFacility()
                                }}
                            >
                                Terminate
                            </Menu.Item>
                        ) : (
                            <>
                                <Menu.Item disabled={loading}
                                    icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                    onClick={() => {
                                        amendFacility(row.id, row.amendmentDate === row.startDate ? 'edit' : 'amend')
                                    }}
                                >
                                    Edit
                                </Menu.Item>

                            </>
                        )
                    ) : null}

                    {row?.status?.toLowerCase() == 'draft' && row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
                            onClick={() => submitCurrentFacilityAmendment()}
                        >
                            Submit For Approval
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'draft' && !row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
                            onClick={() => submitCurrentFacility()}
                        >
                            Submit For Approval
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'submitted' && row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
                            onClick={() => approveCurrentFacilityAmendment()}
                        >
                            Approve
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'submitted' && !row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
                            onClick={() => approveCurrentFacility()}
                        >
                            Approve
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'approved' ||
                        row?.amendment?.status?.toLowerCase() === '' ? (
                        <>
                            <Menu.Item disabled={loading}
                                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                onClick={() => {
                                    amendFacility(row.id, 'edit')
                                }}
                            >
                                Edit
                            </Menu.Item>
                            <Menu.Item disabled={loading}
                                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                onClick={() => {
                                    amendFacility(row.id, 'amend')
                                }}
                            >
                                Amendment
                            </Menu.Item>
                            <Menu.Item disabled={loading}
                                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                onClick={() => {
                                    terminateFacility()
                                }}
                            >
                                Terminate
                            </Menu.Item>
                        </>
                    ) : null}

                    {extended ? (
                        <>
                            {row?.status?.toLowerCase() == 'approved' ||
                                row?.amendment?.status?.toLowerCase() === '' ? (
                                <>
                                    <Menu.Item disabled={loading}
                                        icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                        onClick={() => openLoanForm()}
                                    >
                                        Create Loan
                                    </Menu.Item>
                                    <Menu.Item disabled={loading}
                                        icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                                        onClick={() => setLetterOfCreditOpen(true)}
                                    >
                                        Create Letter of Credit
                                    </Menu.Item>
                                </>
                            ) : null}
                        </>
                    ) : null}

                    {row?.status?.toLowerCase() != 'approved' ? (
                        <>
                            <Menu.Divider />
                            <Menu.Label>Danger zone</Menu.Label>
                        </>
                    ) : null}

                    {row?.status?.toLowerCase() === 'draft' && !row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name="settings-outline" />}
                            onClick={() => {
                                setShowDeleteModal(true);
                                setDeleteType('facility');
                            }}
                        >
                            Delete
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() === 'draft' && row?.amendment ? (
                        <Menu.Item disabled={loading}
                            icon={<IonIcon className="action-ion-icon" name="trash-outline" />}
                            color="red"
                            onClick={() => {
                                setShowDeleteModal(true);
                                setDeleteType('amendment');
                            }}
                        >
                            Delete
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'submitted' && row?.amendment ? (
                        <Menu.Item disabled={loading}
                            color="red"
                            icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
                            onClick={() => rejectCurrentFacilityAmendment()}
                        >
                            Reject
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() == 'submitted' && !row?.amendment ? (
                        <Menu.Item disabled={loading}
                            color="red"
                            icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
                            onClick={() => rejectCurrentFacility()}
                        >
                            Reject
                        </Menu.Item>
                    ) : null}

                    <Menu.Item disabled={loading}
                        onClick={() => displayHistory()}
                        icon={<IonIcon className="action-ion-icon" name='arrow-undo-circle-outline' />}
                    >
                        History
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
            <Modal
                size="90%"
                opened={openHistory}
                onClose={() => setOpenHistory(false)}
            >
                {facility && (
                    <HistoryForm
                        facilityId={facility.id ?? ''}
                        close={() => setOpenHistory(false)}
                    />
                )}
            </Modal>
            <>
                <Modal
                    className="modal-body modal-body-template fit-content-modal"
                    opened={loanOpen}
                    onClose={() => setLoanOpen(false)}
                    centered={true}
                    size="77%"
                >
                    <UpdatedLoanScreen
                        loan={undefined}
                        facility={facility}
                        step={0}
                        onClose={() => setLoanOpen(false)}
                    />
                </Modal>

            </>
            <>
                <Modal
                    className="modal-body modal-body-template fit-content-modal"
                    size="xl"
                    opened={letterOfCreditOpen}
                    onClose={() => setLetterOfCreditOpen(false)}
                >
                    <LetterOfCreditForm close={() => setLetterOfCreditOpen(false)} />
                </Modal>
            </>
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <DeleteConfirmation
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteModal(false)}
                    message={
                        deleteType === 'facility'
                            ? 'Delete This Facility?'
                            : deleteType === 'amendment'
                                ? 'Delete This Facility Amendment?'
                                : 'Are you sure?'
                    }
                />
            )}
            <PlaceHoldModal
                opened={showPlaceHold}
                close={() => setShowPlaceHold(false)}
                entityNoun="facility"
                onSubmit={(payload: HoldPayload) =>
                    placeFacilityHoldAction(row.id, payload, dispatch)
                }
            />
            <LiftHoldModal
                opened={showLiftHold}
                close={() => setShowLiftHold(false)}
                entityNoun="facility"
                hold={activeFacilityHold}
                onSubmit={(liftReason?: string) =>
                    liftFacilityHoldAction(row.id, liftReason, dispatch)
                }
            />
        </div>
    )
}
